import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Typography,
    useTheme,
    CircularProgress,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";

import { setPaymentComponent } from "../../store/Vanlifer/vanliferComponentsSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import HereMapTourComponent from "./HereMapTourComponent";
import { asyncReserveTourSilent, setTour, TourDistance } from "../../store/Vanlifer/tourSlice";
import { TourOverview } from "../../services/restserver-openapi";

type TourCardProp = {
    tour: TourOverview;
    waypoints: string[];
    index: number;
    onTourSelected?: (tourId: number) => void;
};

function TourCard({ tour, waypoints, index, onTourSelected }: TourCardProp) {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const HERE_MAP_ID = process.env.REACT_APP_HERE_MAP_ID!;
    const tourDistances = useAppSelector((state) => state.tour.tourDistances);
    const userEntitlements = useAppSelector((state) => state.user.entitlements);

    const [distance, setDistance] = useState(0);
    const [showMap, setShowMap] = useState(false);

    const canBookTour = useMemo(() => {
        return userEntitlements.some(
            (entitlement) => entitlement.featureId === "vanliferbooktour" && entitlement.value === "true"
        );
    }, [userEntitlements]);

    // ✅ robust: normalize tourId to number | null
    const tourId = typeof tour.tourId === "number" ? tour.tourId : null;

    // status helper
    const statusStr = String(tour.status ?? "");
    const isFinal = statusStr === "BOOKED" || statusStr === "CANCELED";
    const isReserved = statusStr === "RESERVED";

    useEffect(() => {
        const delay = index * 800;
        const timeout = setTimeout(() => setShowMap(true), delay);
        return () => clearTimeout(timeout);
    }, [index]);

    const findObjectById = useCallback(
        (id: number): TourDistance => {
            return tourDistances?.find((obj) => obj.tourId === id) || { tourId: 0, distance: 0 };
        },
        [tourDistances]
    );

    useEffect(() => {
        if (tourId && tourId > 0) {
            const tourDistance = findObjectById(tourId);
            setDistance(tourDistance.distance);
        }
    }, [findObjectById, tourId]);

    const shortenToTwoDecimals = (num: number) => parseFloat(num.toFixed(2));

    const handleClick = async () => {
        if (!tourId) return;

        dispatch(setTour(tour));

        if (!canBookTour) {
            dispatch(setPaymentComponent(true));
            return;
        }

        // ✅ nur die ausgewählte Tour reservieren (falls nötig), bevor die TourPage geöffnet wird
        if (!isFinal && !isReserved) {
            try {
                await dispatch(asyncReserveTourSilent(tourId)).unwrap();
            } catch {
                // Reservierung fehlgeschlagen -> trotzdem öffnen (TourPage kann zusätzlich absichern)
            }
        }

        onTourSelected?.(tourId);
        navigate(`/Vanlifer/Tour/${tourId}`);
    };

    return (
        <Card
            sx={{
                width: 1,
                height: 1,
                display: "flex",
                flexDirection: "column",
                boxShadow: 4,
                bgcolor: "transparent",
                backgroundImage: "none",
                "&:hover": {
                    transform: `translateY(-${theme.spacing(0.5)})`,
                },
                opacity: tourId ? 1 : 0.65,
            }}
        >
            <CardActionArea onClick={handleClick} disabled={!tourId}>
                <CardMedia
                    sx={{
                        position: "relative",
                        height: 200,
                        overflow: "hidden",
                        borderRadius: 2,
                    }}
                >
                    {!showMap ? (
                        <Box height={200} display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                            <Typography variant="caption" sx={{ marginLeft: 1 }}>
                                Karte wird geladen...
                            </Typography>
                        </Box>
                    ) : tourId ? (
                        <HereMapTourComponent
                            apiKey={HERE_MAP_ID}
                            waypoints={waypoints}
                            height={"200px"}
                            editorSite={false}
                            tourId={tourId}
                        />
                    ) : (
                        <Box height={200} display="flex" alignItems="center" justifyContent="center">
                            <Typography variant="caption" color="text.secondary">
                                Tour-ID fehlt
                            </Typography>
                        </Box>
                    )}
                </CardMedia>

                <CardContent>
                    <Box marginTop={2}>
                        <Typography fontWeight={700}>{tour.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Tour ID: {tourId ?? "—"}
                        </Typography>
                    </Box>

                    <Box marginTop={2}>
                        <Typography variant="body1" color="text.secondary">
                            Anzahl Unterkünfte: {tour.stays?.length ?? 0}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Gesamtkilometer: {distance} km
                        </Typography>
                    </Box>

                    <Box marginTop={2} display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <StarIcon color="secondary" />
                            <Typography fontWeight={700} color="text.secondary" sx={{ padding: 0.5 }}>
                                {tour.rating ? shortenToTwoDecimals(tour.rating) : 0}
                            </Typography>
                        </Box>
                        <Typography fontWeight={700} color="text.secondary" sx={{ padding: 0.5 }}>
                            {tour.price} CHF
                        </Typography>
                    </Box>

                    {/* optional: kleine Statusanzeige */}
                    <Box mt={1}>
                        <Typography variant="caption" color="text.secondary">
                            Status: {statusStr || "—"}
                        </Typography>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

export default TourCard;
